import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import LanguageToggle from './LanguageToggle';

const links: { href: string; key: string }[] = [
  { href: '#home', key: 'nav.home' },
  { href: '#details', key: 'nav.details' },
  { href: '#confirm-attendance', key: 'nav.confirm' },
  { href: '#location', key: 'nav.location' },
  { href: '#gallery', key: 'nav.gallery' },

];

export default function NavHeader() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <header
      className={`sticky top-0 z-40 backdrop-blur-sm transition-colors ${
        scrolled
          ? 'bg-brand-paper/90 border-b border-brand-gold/20'
          : 'bg-brand-paper/55'
      }`}
    >
      <div className="mx-auto max-w-6xl px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <a href="#home" className="font-script text-3xl text-brand-gold leading-none">
          L &amp; C
        </a>

        <nav aria-label="Primary" className="hidden lg:flex items-center gap-6 xl:gap-8">
          {links.map((l) => (
            <a
              key={l.key}
              href={l.href}
              className="font-sans text-[0.7rem] xl:text-xs tracking-widest uppercase text-brand-gold hover:text-brand-gold-deep transition-colors"
            >
              {t(l.key)}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href="#confirm-attendance"
            className="inline-flex items-center justify-center rounded-full bg-brand-gold px-5 py-1.5 font-sans text-[0.7rem] xl:text-xs font-medium tracking-widest uppercase text-white hover:bg-brand-gold-deep transition-colors"
          >
            {t('nav.rsvp')}
          </a>
          <span aria-hidden="true" className="block h-4 w-px bg-brand-ink/30" />
          <LanguageToggle />
        </div>

        <button
          type="button"
          className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full text-brand-gold"
          aria-label={open ? t('nav.closeMenu') : t('nav.openMenu')}
          aria-expanded={open}
          aria-controls="mobile-menu"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <>
          <div
            className="fixed inset-0 bg-brand-ink/30 lg:hidden"
            aria-hidden="true"
            onClick={() => setOpen(false)}
          />
          <div
            id="mobile-menu"
            role="dialog"
            aria-modal="true"
            className="lg:hidden absolute left-0 right-0 top-16 bg-brand-paper border-b border-brand-gold/20 shadow-card"
          >
            <ul className="flex flex-col">
              {links.map((l) => (
                <li key={l.key}>
                  <a
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className="block px-6 py-4 font-sans text-xs tracking-widest uppercase text-brand-gold border-b border-brand-gold/10"
                  >
                    {t(l.key)}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="#confirm-attendance"
                  onClick={() => setOpen(false)}
                  className="mx-6 my-4 inline-flex items-center justify-center rounded-full bg-brand-gold px-6 py-2 font-sans text-xs tracking-widest uppercase text-white"
                >
                  {t('nav.rsvp')}
                </a>
              </li>
              <li className="px-6 py-4 border-t border-brand-gold/10">
                <LanguageToggle />
              </li>
            </ul>
          </div>
        </>
      )}
    </header>
  );
}
